import {Bar, BarPattern, SegmentWithoutChildren, TooltipDataCallback} from 'common/charts/visualization/bar-viz/types';
import {DateKeyedFunnelSegments, FunnelBookmark, FunnelSegment, FunnelStep} from 'common/report/models/funnels/types';
import {getFunnelStep, getMedian} from 'common/report/models/funnels/bar-chart/utils';
import {
  getCalendarDateLabels,
  getMedianComparator,
  getRenamedOrderedSegmentSteps,
} from 'common/report/models/funnels/utils';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {TooltipBulletStyle, TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {createFunnelDataWithRename} from 'common/report/data/funnels/utils';
import {getSelectedSegments} from 'common/report/util/funnels/utils';
import {FunnelQueryResponse} from 'common/report/data/funnels/api-types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import * as colors from 'common/util/colors';

export function processFunnelForChartWidget(
  bookmark: FunnelBookmark,
  funnelsResponse: FunnelQueryResponse,
  customEvents: CustomEventDefinition[],
  projectId: string,
  projectAsOfDate: string,
  stepIndex: number | null, // null is a valid stepIndex for ttc
  medianAccessor: keyof FunnelStep,
): {
  stateUpdates: {
    stepIndex: number;
    query: FunnelsQuery;
    selectedSegments: ColorChoice[];
    formattedCompareData: any;
    dateRangeLabels: string[];
    formattedData: FunnelSegment[];
  };
  legendSegmentItems: SegmentItem[];
  legendSelectedSegments: ColorChoice[];
} {
  const query = FunnelsQuery.fromBookmark(bookmark, {
    customEvents,
    // TODO - change the types for funnelsquery to take string projectIDs in a separate refactor
    projectId: parseInt(projectId, 10),
  });

  const segmentData: DateKeyedFunnelSegments[] = createFunnelDataWithRename(funnelsResponse.data, query);
  const hasComparisonData = !!funnelsResponse.compare_data;
  const comparisonSegmentData: DateKeyedFunnelSegments[] = hasComparisonData
    ? createFunnelDataWithRename(funnelsResponse.compare_data, query)
    : null;

  let formattedCompareData = null;

  if (hasComparisonData) {
    formattedCompareData = getRenamedOrderedSegmentSteps(
      query,
      comparisonSegmentData,
      getMedianComparator(stepIndex, medianAccessor),
    );
  }

  const formattedData = getRenamedOrderedSegmentSteps(
    query,
    segmentData,
    getMedianComparator(stepIndex, medianAccessor),
  );

  const selectedSegments: ColorChoice[] = getSelectedSegments(
    bookmark.selectedSegments,
    bookmark.selectMode,
    formattedData,
  );

  const stateUpdates = {
    dateRangeLabels: getCalendarDateLabels(query, comparisonSegmentData, projectAsOfDate),
    formattedData,
    formattedCompareData,
    query,
    selectedSegments,
    stepIndex,
  };

  const legendSegmentItems: SegmentItem[] = formattedData.map((funnelSegment, idx) => ({
    color: colors.getChartSegmentColorCombo(idx).primary,
    id: funnelSegment.prop,
    label: funnelSegment.propName,
  }));

  return {
    stateUpdates,
    legendSegmentItems,
    legendSelectedSegments: bookmark.selectedSegments,
  };
}

export function generateMedianBarAndLegendSegments({
  selectedSegments,
  formattedData,
  formattedCompareData,
  medianAccessor,
  hasComparisonData,
  stepIndex,
  medianFormatter,
}: {
  selectedSegments: SegmentItem[];
  formattedData: FunnelSegment[];
  formattedCompareData: FunnelSegment[];
  medianAccessor: keyof FunnelStep;
  hasComparisonData: boolean;
  stepIndex: number;
  medianFormatter?: (median: number) => string;
}): SegmentWithoutChildren[] {
  const segments: SegmentWithoutChildren[] = [];

  // todo(sam) this won't work for unflattened segments
  selectedSegments.forEach((segmentItem) => {
    const {id, color} = segmentItem;
    const bars: Bar[] = [];

    const funnelSegment = formattedData.find((segment: FunnelSegment) => segment.prop === id);

    const step = getFunnelStep(funnelSegment.steps, stepIndex);
    const median = getMedian(step, medianAccessor);
    const medianLabel = medianFormatter ? medianFormatter(median) : median;

    bars.push({
      segments: [
        {
          value: median || 0, // TTC and Freq charts want to display an undefined value with n/a, which the provided formatter will take care of
          label: medianLabel,
          color,
        },
      ],
    });

    if (hasComparisonData) {
      const comparisonSegment = formattedCompareData.find((segment: FunnelSegment) => segment.prop === id);

      const comparisonStep = getFunnelStep(comparisonSegment.steps, stepIndex);
      const comparisonMedian = getMedian(comparisonStep, medianAccessor);
      const comparisonMedianLabel = medianFormatter ? medianFormatter(comparisonMedian) : comparisonMedian;

      bars.push({
        segments: [
          {
            value: comparisonMedian || 0,
            label: comparisonMedianLabel,
            pattern: BarPattern.Striped,
            color,
          },
        ],
      });
    }

    const segment: SegmentWithoutChildren = {
      label: funnelSegment.propName,
      bars,
      segmentName: funnelSegment.propName,
    };

    segments.push(segment);
  });

  return segments;
}

export function createTooltipDataCallback(
  formattedCompareData,
  formatter: (label: string) => TooltipRow[],
  dateRangeLabels?: string[],
): TooltipDataCallback {
  return (segment, _bar, barSegment) => [
    {
      rows: [
        {
          items: [
            {
              text: segment.label,
              type: TooltipItemType.Strong,
            },
          ],
        },
      ],
    },
    ...(formattedCompareData
      ? [
          {
            bulletStyle: TooltipBulletStyle.Solid,
            bulletColor: barSegment.color,
            rows: [
              {
                items: [{text: dateRangeLabels[0], type: TooltipItemType.Detail}],
              },
              ...formatter(`${segment.bars[0].segments[0].label}`),
            ],
          },
        ]
      : [
          {
            bulletStyle: TooltipBulletStyle.Solid,
            bulletColor: barSegment.color,
            rows: [...formatter(`${segment.bars[0].segments[0].label}`)],
          },
        ]),
    ...(formattedCompareData
      ? [
          {}, // empty row for spacing
          {
            bulletStyle: TooltipBulletStyle.Dash,
            bulletColor: barSegment.color,
            rows: [
              {
                items: [{text: dateRangeLabels[1], type: TooltipItemType.Detail}],
              },
              ...formatter(`${segment.bars[1].segments[0].label}`),
            ],
          },
        ]
      : []),
  ];
}
